/* eslint-disable @typescript-eslint/no-explicit-any */
import { localNow } from "@/shared/local-now";
import { DataTable } from "@/components/data-table";
import { Form, SchemaFormHook, useSchemaForm } from "@/components/form";
import { Dialog, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import { useModalHandlerContext } from "@/components/modal/contexts/modal-handler-context";
import { usePrinter } from "@/components/print";
import { getCliente } from "@/features/auxiliar/clientes/api/get-cliente";
import { getVendedor } from "@/features/auxiliar/vendedores/api/get-vendedor";
import { useCreateAndPayVentaMutation } from "@/features/cajas/caja-actual/api/create-and-pay-venta";
import { useCreateAndPayVentaConFEMutation } from "@/features/cajas/caja-actual/api/create-and-pay-venta-fe";
import CajaPagoForm, { cajaPagoFormSchema } from "@/features/cajas/caja-actual/components/caja-pago-form";
import { useCajaContext } from "@/features/cajas/caja-actual/contexts/caja-context";
import { useConfigurationContext } from "@/features/configuration/contexts/configuration-context";
import { useOperationalMode } from "@/features/configuration/hooks/use-operational-mode";
import { useAddVentaMutation } from "@/features/inventario/ventas/api/add-venta";
import { useCreateVentaConFECreditoMutation } from "@/features/inventario/ventas/api/create-venta-con-fe-credito";
import { VentaForm, ventaFormSchema } from "@/features/inventario/ventas/components/venta-form";
import { ventaConFEFormSchema } from "@/features/inventario/ventas/schemas/venta-con-fe-form-schema";
import VentaPrintForm from "@/features/inventario/ventas/components/venta-print-form";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { notify, notifyError, SilentSubmitError } from "@/shared/notification";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import { Permiso } from "@/shared/types/permiso";
import { validarStockItems, validarStockLocal } from "@/features/inventario/articulos/utils/validar-stock-items";
import { EmisorConfigRequiredDialog, useCheckEmisorConfig } from "@/features/facturacion-electronica/components/config-required-alert";
import { Container, Stack, Typography, Checkbox, FormControlLabel, Button, Box, RadioGroup, Radio, TextField, FormLabel } from "@mui/material";
import { z } from "zod/v4";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useVentaDraft } from "@/features/inventario/ventas/hooks/use-venta-draft";
import VentaDraftRestore from "@/features/inventario/ventas/components/venta-draft-restore";
import { useEmisorConfig } from "@/features/facturacion-electronica/hooks/use-emisor-config";
import { buildCreateAndPayFERequest } from "@/features/cajas/caja-actual/utils/build-create-and-pay-fe-request";
import { buildCreateVentaFERequest } from "@/features/cajas/caja-actual/utils/build-create-venta-fe-request";
import useKudePrinter, { PrintKudeOptions } from "@/features/facturacion-electronica/hooks/use-kude-printer";
import { MonedaOperacionField } from "@/features/cajas/caja-actual/components/moneda-operacion-field";
import { OperationalMode } from "@/shared/types/operational-mode";
import { useDepositoContext } from "@/features/inventario/deposito-actual/contexts/deposito-context";

const getVentaDefaultValues = (facturacionEnabled: boolean, genericClientId: string) =>
    facturacionEnabled ? {
        clienteId: genericClientId,
        iTipTra: 1,
        iTipEmi: 1,
        iIndPres: 1,
        dCondTiCam: 1
    } as any : {
        clienteId: genericClientId
    }

// Resetea el form de venta preservando el vendedor y el depósito asignado
// (el reset los vacía y no hay otro efecto que los reponga).
const resetVentaForm = (form: any, deleteCurrentDraft: () => void, depositoAsignadoId?: string) => {
    const vendedorId = form.getFieldValue("vendedorId");
    form.reset();
    form.setFieldValue("vendedorId", vendedorId);
    if (depositoAsignadoId) form.setFieldValue("depositoId", depositoAsignadoId);
    deleteCurrentDraft();
};

// Precarga de una venta a partir de un presupuesto (navegación desde "Ver presupuesto").
type VentaPreload = { clienteId?: string, vendedorId?: string, items?: any[] };

const applyVentaPreload = (form: any, preload: VentaPreload) => {
    if (preload.vendedorId) form.setFieldValue("vendedorId", preload.vendedorId);
    if (preload.clienteId) form.setFieldValue("clienteId", preload.clienteId);
    form.setFieldValue("credito", false);
    form.setFieldValue("presupuesto", false);
    form.setFieldValue("items", preload.items ?? []);
}

// Aplica el preload de location.state una sola vez y limpia el state para no re-precargar.
const useVentaPreload = (form: any) => {
    const location = useLocation();
    const appliedRef = useRef(false);

    useEffect(() => {
        if (appliedRef.current) return;
        const preload = (location.state as any)?.preloadVenta as VentaPreload | undefined;
        if (!preload) return;

        appliedRef.current = true;
        applyVentaPreload(form, preload);
        window.history.replaceState(null, "");
    }, [location.state]);
}

const handleCreateVentaConFECredito = async (
    value: any,
    monedaBase: any,
    createVentaConFECreditoMutation: any,
    printKude: (opts: PrintKudeOptions) => Promise<void>,
    dEst?: string,
    dPunExp?: string,
    monedaOperacionId?: string,
    dTiCam?: number,
    tipoCredito?: "plazo" | "cuotas",
    plazoDias?: string,
    numeroCuotas?: number
) => {

    const requestData = buildCreateVentaFERequest({
        ventaFormValue: value,
        monedaBase,
        dEst: dEst || "",
        dPunExp: dPunExp || "",
        monedaOperacionId,
        dTiCam,
        tipoCredito,
        plazoDias,
        numeroCuotas
    });

    const result = await createVentaConFECreditoMutation.mutateAsync(requestData as any);

    await printKude({
        documentTitle: `KUDE Venta N°${result.venta.numeroVenta}`,
        kudeBase64: result.facturaElectronica?.kudeBase64,
        ventaId: result.venta.id
    });

    notify(`La venta se ha creado correctamente. Se creo una factura electrónica N° ${result.facturaElectronica?.numeroDE}.`);

    return result;
}

export default function NuevaVentaPage() {
    const { paySeparately } = useConfigurationContext();

    return <Container flex={1} component={Stack} maxWidth="xl" gap={3} paddingY={3}>
        <Stack direction={"row"} alignItems={"center"}>
            <Typography component={"h1"} variant="h5">Nueva Venta</Typography>
        </Stack>

        {paySeparately ? <AddVentaForm /> : <AddAndPayVentaForm />}
    </Container>
}

// Formulario para crear venta sin pago
function AddVentaForm() {
    const printer = usePrinter();
    const { genericClientId } = useConfigurationContext();
    const { facturacionEnabled, mode } = useOperationalMode();
    const { monedaBase } = useMonetizacion();
    const createVentaConFECreditoMutation = useCreateVentaConFECreditoMutation();
    const { config } = useEmisorConfig();
    const openModal = useOpenModal();
    const ventaMutation = useAddVentaMutation();
    const hasAllowNegativeStock = useHasPermisos(Permiso.AllowNegativeStock);
    const { depositoData } = useDepositoContext();
    const schema = facturacionEnabled ? ventaConFEFormSchema : ventaFormSchema;

    const form = useSchemaForm({
        schema: schema,
        onSubmit: async ({ value, formApi }) => {
            const isCredito = value.credito === true;
            const isPresupuesto = value.presupuesto === true;

            // Revalidar stock contra el depósito al confirmar (bloquea si falta).
            if (!isPresupuesto && !hasAllowNegativeStock && value.depositoId) {
                // Validación local con el stock guardado en cada ítem; solo consulta la API
                // para los ítems sin dato conocido (drafts viejos, precarga de presupuesto).
                const { faltantes, sinDato } = validarStockLocal(value.items);
                if (sinDato.length > 0)
                    faltantes.push(...await validarStockItems(sinDato, value.depositoId));
                if (faltantes.length > 0) {
                    notifyError("Hay artículos sin stock suficiente. Verifique los artículos marcados en la lista.");
                    return;
                }
            }

            const resetKeepVendedor = () => resetVentaForm(formApi, deleteCurrentDraft, depositoData?.id);

            let venta;
            if (facturacionEnabled && isCredito && !isPresupuesto) {
                openModal("confirmFECredito", {
                    ventaRequest: value,
                    onSuccess: resetKeepVendedor
                });
                return;
            } else {
                venta = await (ventaMutation.mutateAsync as any)(value);
                openModal("printVenta", venta);
                notify("Se ha creado la venta correctamente.");
                resetKeepVendedor();
            }
        },
        defaultValues: getVentaDefaultValues(facturacionEnabled, genericClientId)
    })

    const { otherDrafts, deleteCurrentDraft, deleteDraft } = useVentaDraft(form);

    useVentaPreload(form);

    const handleRestore = (draft: any) => {
        const v = draft.values;
        form.setFieldValue("vendedorId", v.vendedorId ?? "");
        form.setFieldValue("clienteId", v.clienteId ?? "");
        form.setFieldValue("fecha", v.fecha ?? localNow());
        form.setFieldValue("credito", v.credito ?? false);
        form.setFieldValue("presupuesto", v.presupuesto ?? false);
        form.setFieldValue("items", (v.items ?? []).map((i: any) => ({ ...i, stockDisponible: undefined })));
        deleteDraft(draft.id);
    };

    return <>
        <VentaForm form={form as any} flex={1} onAfterReset={deleteCurrentDraft}
            draftRestoreSlot={<VentaDraftRestore drafts={otherDrafts} onRestore={handleRestore} onDelete={deleteDraft} />}
        />
        <printer.Printer />
        <Dialog code="printVenta" size="xs">
            <ModalValueExtractor>
                {venta => <>
                    <ModalHeader>Imprimir Venta N°{venta.numeroVenta}</ModalHeader>
                    <VentaPrintForm venta={venta} padding={3} />
                </>}
            </ModalValueExtractor>
        </Dialog>
        <Dialog code="confirmFECredito" size="xs">
            <ModalValueExtractor>
                {(data) => <ConfirmFECreditoDialog
                    ventaRequest={data.ventaRequest}
                    monedaBase={monedaBase}
                    createVentaConFECreditoMutation={createVentaConFECreditoMutation}
                    createMutation={ventaMutation}
                    config={config}
                    mode={mode}
                    onSuccess={data.onSuccess}
                />}
            </ModalValueExtractor>
        </Dialog>
    </>
}

const createAndPayVentaFormSchema = z.object({
    ventaRequest: ventaFormSchema,
    pagoRequest: cajaPagoFormSchema
})

const createAndPayVentaConFEFormSchema = z.object({
    ventaRequest: ventaConFEFormSchema,
    pagoRequest: cajaPagoFormSchema
})

// Formulario para crear y pagar venta
function AddAndPayVentaForm() {
    const openModal = useOpenModal();
    const { isCajaOpen, hasCajaAssigned } = useCajaContext();
    const { facturacionEnabled, mode } = useOperationalMode();
    const { monedaBase, transferenciaMetodoBase, cotizacion, transferenciaMetodos } = useMonetizacion();
    const { genericClientId } = useConfigurationContext();
    const { config } = useEmisorConfig();
    const checkEmisorConfig = useCheckEmisorConfig();
    const hasAllowNegativeStock = useHasPermisos(Permiso.AllowNegativeStock);
    const { depositoData } = useDepositoContext();

    const printer = usePrinter();
    const { printKude, Printer: KudePrinter } = useKudePrinter();
    const createAndPayMutation = useCreateAndPayVentaMutation();
    const createAndPayFEMutation = useCreateAndPayVentaConFEMutation();
    const createVentaConFECreditoMutation = useCreateVentaConFECreditoMutation();
    const createMutation = useAddVentaMutation();

    const formSchema = facturacionEnabled ? createAndPayVentaConFEFormSchema : createAndPayVentaFormSchema;
    const ventaSchema = facturacionEnabled ? ventaConFEFormSchema : ventaFormSchema;

    const form = useSchemaForm({
        schema: formSchema,
        onSubmit: async ({ value }) => {

            const isCredito = value.ventaRequest.credito === true;
            const isPresupuesto = value.ventaRequest.presupuesto === true;
            const feRequestedPago = !!value.pagoRequest?.pago?.facturacionElectronica;
            const feRequested = facturacionEnabled && feRequestedPago;

            // Presupuesto o sin pago
            if (value.pagoRequest == null || isPresupuesto) {
                const venta = await createMutation.mutateAsync(value.ventaRequest);
                openModal("printVenta", venta);
                notify("La venta se ha creado correctamente.");
                resetVentaForm(ventaForm, deleteCurrentDraft, depositoData?.id);
                return;
            }

            // Crédito
            if (isCredito) {
                if (facturacionEnabled) {
                    openModal("confirmFECredito", {
                        ventaRequest: value.ventaRequest,
                        onSuccess: () => resetVentaForm(ventaForm, deleteCurrentDraft, depositoData?.id)
                    });
                    return;
                }

                // Crédito sin FE
                const venta = await createMutation.mutateAsync(value.ventaRequest);
                openModal("printVenta", venta);
                notify("La venta se ha creado correctamente.");
                resetVentaForm(ventaForm, deleteCurrentDraft, depositoData?.id);
                return;
            }

            // Contado
            if (feRequested) {
                checkEmisorConfig(config);

                const requestData = buildCreateAndPayFERequest({
                    ventaRequest: value.ventaRequest,
                    pagoRequest: value.pagoRequest,
                    monedaBase,
                    cotizacion,
                    transferenciaMetodos,
                    dEst: config?.establecimiento ?? "",
                    dPunExp: config?.puntoExpedicion ?? ""
                });

                const pago = await createAndPayFEMutation.mutateAsync(requestData);
                const fe = pago?.data?.facturaElectronica;

                if (fe) {
                    await printKude({
                        documentTitle: `KUDE N°${fe.numeroDE}`,
                        kudeBase64: fe.kudeBase64,
                        ventaId: pago?.data?.ventaId
                    });
                    notify(`La venta se ha creado y pagado correctamente. Se creo una factura electrónica N° ${fe.numeroDE}.`);
                } else {
                    notify("La venta se ha creado y pagado correctamente.");
                }
                return;
            }

            // Contado sin FE
            const pago = await createAndPayMutation.mutateAsync({
                ...value,
                ventaRequest: {
                    ...value.ventaRequest,
                    saltarFE: !feRequestedPago && mode === OperationalMode.GestionConFacturacion
                }
            });
            const venta = pago.venta;
            openModal("printVenta", venta);
            notify("La venta se ha creado y pagado correctamente.");
        }
    });

    const ventaForm = useSchemaForm({
        schema: ventaSchema,
        onSubmit: async ({ value }) => {
            // Revalidar stock contra el depósito al confirmar (bloquea si falta).
            if (!value.presupuesto && !hasAllowNegativeStock && value.depositoId) {
                // Validación local con el stock guardado en cada ítem; solo consulta la API
                // para los ítems sin dato conocido (drafts viejos, precarga de presupuesto).
                const { faltantes, sinDato } = validarStockLocal(value.items);
                if (sinDato.length > 0)
                    faltantes.push(...await validarStockItems(sinDato, value.depositoId));
                if (faltantes.length > 0) {
                    notifyError("Hay artículos sin stock suficiente. Verifique los artículos marcados en la lista.");
                    return;
                }
            }

            form.setFieldValue("ventaRequest", value);
            if (!value.credito && !value.presupuesto) {
                const [cliente, vendedor] = await Promise.all([
                    getCliente(value.clienteId),
                    getVendedor(value.vendedorId)
                ])
                const venta = {
                    total: value.items.reduce((prev, curr) => prev + curr.precioUnitario * curr.cantidad, 0),
                    clienteId: cliente.id,
                    clienteNombre: cliente.nombre,
                    clienteNumDocumento: cliente.numDocumento,
                    vendedorId: vendedor.id,
                    vendedorNombre: vendedor.nombre,
                    credito: value.credito,
                    presupuesto: value.presupuesto,
                };
                openModal("pagarVenta", venta);
            } else {
                await form.handleSubmit();
            }
        },
        defaultValues: getVentaDefaultValues(facturacionEnabled, genericClientId)
    })

    const { otherDrafts, deleteCurrentDraft, deleteDraft } = useVentaDraft(ventaForm);

    useVentaPreload(ventaForm);

    const handleRestoreDraft = (draft: any) => {
        const v = draft.values;
        ventaForm.setFieldValue("vendedorId", v.vendedorId ?? "");
        ventaForm.setFieldValue("clienteId", v.clienteId ?? "");
        ventaForm.setFieldValue("fecha", v.fecha ?? localNow());
        ventaForm.setFieldValue("credito", v.credito ?? false);
        ventaForm.setFieldValue("presupuesto", v.presupuesto ?? false);
        // Limpiar stockDisponible para forzar la revalidación contra el depósito actual.
        ventaForm.setFieldValue("items", (v.items ?? []).map((i: any) => ({ ...i, stockDisponible: undefined })));
        deleteDraft(draft.id);
    };

    if (!hasCajaAssigned) return <DataTable flex={1}>
        <DataTable.Content fit="flex" emptyText="Debes tener una caja asignada para poder crear una venta." />
    </DataTable>

    if (!isCajaOpen) return <DataTable flex={1}>
        <DataTable.Content fit="flex" emptyText="La caja debe estar abierta para poder crear una venta." />
    </DataTable>

    return <Form form={form} flex={1}>
        <VentaForm form={ventaForm} flex={1} onAfterReset={deleteCurrentDraft}
            draftRestoreSlot={<VentaDraftRestore drafts={otherDrafts} onRestore={handleRestoreDraft} onDelete={deleteDraft} />}
        />
        <Dialog code="pagarVenta" size="md" fullHeight>
            <ModalHeader>Pagar Venta</ModalHeader>
            <ModalValueExtractor>
                {venta => <SchemaFormHook
                    schema={cajaPagoFormSchema}
                    onSubmit={async ({ value }) => {
                        form.setFieldValue("pagoRequest", value);
                        await form.handleSubmit();
                        resetVentaForm(ventaForm, deleteCurrentDraft, depositoData?.id);
                    }}
                    defaultValues={{
                        pago: {
                            items: [{ transferenciaMetodoId: transferenciaMetodoBase.id, monto: { items: [{ monedaId: monedaBase.id, cantidad: venta.total }] } }],
                            facturacionElectronica: true,
                        },
                        vuelto: { items: [{ transferenciaMetodoId: transferenciaMetodoBase.id, monto: { items: [{ monedaId: monedaBase.id, cantidad: 0 }] } }] },
                    }}
                    submitOnce
                    closeModalOnSubmit
                    preventCloseModalWhileSubmitting={false}
                >
                    {pagoForm => <CajaPagoForm venta={venta} form={pagoForm} padding={3} flex={1} />}
                </SchemaFormHook>}
            </ModalValueExtractor>
        </Dialog>
        <printer.Printer />
        <KudePrinter />
        <Dialog code="printVenta" size="xs">
            <ModalValueExtractor>
                {venta => <>
                    <ModalHeader>Imprimir Venta N°{venta.numeroVenta}</ModalHeader>
                    <VentaPrintForm venta={venta} padding={3} />
                </>}
            </ModalValueExtractor>
        </Dialog>
        <Dialog code="confirmFECredito" size="xs">
            <ModalValueExtractor>
                {(data) => <ConfirmFECreditoDialog
                    ventaRequest={data.ventaRequest}
                    createVentaConFECreditoMutation={createVentaConFECreditoMutation}
                    createMutation={createMutation}
                    config={config}
                    mode={mode}
                    onSuccess={data.onSuccess}
                />}
            </ModalValueExtractor>
        </Dialog>
        <EmisorConfigRequiredDialog />
    </Form>
}

// Dialog de confirmación de Facturación Electrónica a Crédito
function ConfirmFECreditoDialog({
    ventaRequest,
    createVentaConFECreditoMutation,
    createMutation,
    config,
    mode,
    onSuccess
}: any) {
    const [facturacionElectronica, setFacturacionElectronica] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { closeModal } = useModalHandlerContext();
    const openModal = useOpenModal();
    const { monedaBase } = useMonetizacion();
    const [monedaOperacion, setMonedaOperacion] = useState(ventaRequest.pago?.monedaOperacionId || monedaBase.id);
    const [conversionGuarani, setConversionGuarani] = useState(ventaRequest.pago?.conversionGuarani ?? 1);
    const [tipoCredito, setTipoCredito] = useState<"plazo" | "cuotas">("plazo");
    const [plazoDias, setPlazoDias] = useState("");
    const [numeroCuotas, setNumeroCuotas] = useState(1);
    const checkEmisorConfig = useCheckEmisorConfig();
    const { printKude, Printer: KudePrinter } = useKudePrinter();

    const handleConfirm = async () => {
        // Validar campos de crédito obligatorios para la FE
        if (facturacionElectronica) {
            if (tipoCredito === "plazo" && !plazoDias.trim()) {
                notify("El plazo es requerido.", { variant: "error" });
                return;
            }
            if (tipoCredito === "cuotas" && (!numeroCuotas || numeroCuotas < 1)) {
                notify("El número de cuotas es requerido.", { variant: "error" });
                return;
            }
        }

        setIsSubmitting(true);
        try {
            if (facturacionElectronica) {
                checkEmisorConfig(config);

                await handleCreateVentaConFECredito(
                    ventaRequest,
                    monedaBase,
                    createVentaConFECreditoMutation,
                    printKude,
                    config?.establecimiento,
                    config?.puntoExpedicion,
                    monedaOperacion,
                    conversionGuarani,
                    tipoCredito,
                    plazoDias,
                    numeroCuotas
                );
            } else {
                const venta = await createMutation.mutateAsync({
                    ...ventaRequest,
                    saltarFE: mode === OperationalMode.GestionConFacturacion
                });
                openModal("printVenta", venta);
                notify("La venta se ha creado correctamente.");
            }

            onSuccess?.();
            closeModal("confirmFECredito");
        } catch (error: any) {
            if (error instanceof SilentSubmitError) return;
            notify(error?.response?.data || "Error al crear la venta", { variant: "error" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        closeModal("confirmFECredito");
    };

    const monedaOperacionValue = {
        monedaOperacionId: monedaOperacion,
        conversionGuarani: conversionGuarani
    };

    const handleMonedaOperacionChange = (val: { monedaOperacionId: string; conversionGuarani: number }) => {
        setMonedaOperacion(val.monedaOperacionId);
        setConversionGuarani(val.conversionGuarani);
    };

    return (
        <Stack>
            <ModalHeader>Confirmar Tipo de Factura</ModalHeader>
            <Stack padding={2} gap={1}>
                <Typography>Si está marcada la casilla se generará una factura electrónica a crédito.</Typography>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={facturacionElectronica}
                            onChange={(e) => setFacturacionElectronica(e.target.checked)}
                        />
                    }
                    label="Facturación Electrónica"
                />
                {facturacionElectronica && (
                    <Stack direction="row" gap={2} paddingTop={1} alignItems="center">
                        <MonedaOperacionField
                            value={monedaOperacionValue}
                            onChange={handleMonedaOperacionChange}
                        />
                    </Stack>
                )}

                {facturacionElectronica && (
                    <Stack gap={1} paddingTop={1}>
                        <FormLabel>Tipo de Crédito</FormLabel>
                        <RadioGroup
                            row
                            value={tipoCredito}
                            onChange={(e) => setTipoCredito(e.target.value as "plazo" | "cuotas")}
                        >
                            <FormControlLabel value="plazo" control={<Radio />} label="Plazo" />
                            <FormControlLabel value="cuotas" control={<Radio />} label="Cuotas" />
                        </RadioGroup>

                        {tipoCredito === "plazo" ? (
                            <TextField
                                label="Plazo"
                                size="small"
                                value={plazoDias}
                                onChange={(e) => setPlazoDias(e.target.value)}
                                placeholder="Ej: 30 días, 2 meses"
                                slotProps={{ htmlInput: { maxLength: 15, minLength: 2 } }}
                            />
                        ) : (
                            <TextField
                                label="Nº Cuotas"
                                size="small"
                                type="number"
                                value={numeroCuotas}
                                onChange={(e) => setNumeroCuotas(Number(e.target.value))}
                                placeholder="1"
                                slotProps={{ htmlInput: { min: 1, maxLength: 3 } }}
                            />
                        )}
                    </Stack>
                )}

                <Box display="flex" gap={2} justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Procesando..." : "Confirmar"}
                    </Button>
                </Box>
            </Stack>
            <KudePrinter />
        </Stack>
    );
}